import { BackgroundMaterial } from '@babylonjs/core/Materials/Background/backgroundMaterial';
import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Color3, Color4, Vector3 } from '@babylonjs/core/Maths/math';
import { Axis, Space } from '@babylonjs/core/Maths/math.axis';
import { Path3D } from '@babylonjs/core/Maths/math.path';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { Scene } from '@babylonjs/core/scene';

import { getTextureUrl } from '../../utils/assetsUrlBuilder';
import Settings from '../settings/settings';

export default class MeshUtils {
  public static createLineCaps = (name: string, start: Vector3, end: Vector3, scene: Scene) => {
    function configureLineCapRendering(mesh, color) {
      mesh.isPickable = false;
      mesh.renderingGroupId = Settings.OVERLAY_RENDERING_GROUP;
      mesh.layerMask = Settings.HIDDEN_ON_SCREENSHOT_CAMERA_MASK;

      const material = new StandardMaterial(name + '-material' + name, scene);
      material.emissiveColor = color;
      material.disableLighting = true;
      mesh.material = material;
    }

    function createDisc(name, color, scene) {
      const mesh = Mesh.CreateCylinder(name, 0.25, 4, 4, 8, 1, scene, true);
      configureLineCapRendering(mesh, color);
      return mesh;
    }

    const points = [start, end];
    const brandColor = Settings.BRAND_COLOR;
    const arrowName = name;

    const line = MeshBuilder.CreateLines(arrowName, { points }, scene);
    line.color = brandColor;
    line.isPickable = false;
    line.renderingGroupId = Settings.OVERLAY_RENDERING_GROUP;
    line.layerMask = Settings.HIDDEN_ON_SCREENSHOT_CAMERA_MASK;

    const path3d = new Path3D(points);
    const tangent = path3d.getTangents()[1];
    const normal = path3d.getNormals()[1];
    const binormal = path3d.getBinormals()[1];

    const lineCapEnd = createDisc(arrowName + '-end-disc', brandColor, scene);
    lineCapEnd.setAbsolutePosition(end);
    lineCapEnd.rotation = Vector3.RotationFromAxis(binormal, tangent, normal);
    line.addChild(lineCapEnd);

    const lineCapStart = createDisc(arrowName + '-start-disc', brandColor, scene);
    lineCapStart.setAbsolutePosition(start);
    lineCapStart.rotation = Vector3.RotationFromAxis(binormal, tangent, normal);
    lineCapStart.rotate(Axis.X, Math.PI, Space.LOCAL);
    line.addChild(lineCapStart);

    return line;
  };

  public static createGround(scene: Scene) {
    const colorValue = Settings.BACKGROUND_COLOR_TONE;
    scene.clearColor = new Color4(colorValue, colorValue, colorValue, 1);

    // outer circle creates illusion of being inside 3D cylinder
    const ground = MeshBuilder.CreateGround(
      'outerCircle',
      { width: 3000, height: 3000, subdivisions: 0 },
      scene,
    );
    const material = this.loadGroundMaterial(scene);
    const colorValue1 = Settings.BACKGROUND_COLOR_TONE;
    material.useRGBColor = false;
    material.primaryColor = new Color3(colorValue1, colorValue1, colorValue1);
    ground.material = material;

    // inner circle reflects lights and shadows
    const ground1 = MeshBuilder.CreateGround(
      'innerCircle',
      { width: 500, height: 500, subdivisions: 0 },
      scene,
    );
    ground1.receiveShadows = true;
    const material1 = this.loadGroundMaterial(scene);
    material1.shadowLevel = 0.85;
    ground1.material = material1;
  }

  private static loadGroundMaterial(scene: Scene) {
    const groundMaterial = new BackgroundMaterial('groundMaterial', scene);
    groundMaterial.diffuseTexture = new Texture(getTextureUrl('backgroundGround.png'), scene);
    groundMaterial.diffuseTexture.hasAlpha = true;
    return groundMaterial;
  }
}
